<template>
    <section class="rulebook-cycles">
        <div class="d-flex flex-column flex-md-row align-items-md-end justify-content-between mb-3">
            <div>
                <h5 class="mb-1">자동 실행 이력</h5>
                <p class="text-muted mb-0">
                    최근 50회차입니다. 매일 03:00 (KST) 회차가 피드백을 모아 판단 기준 후보를 만듭니다.
                </p>
            </div>
            <b-button size="sm"
                      variant="outline-secondary"
                      class="mt-2 mt-md-0"
                      :disabled="loading"
                      @click="loadCycles"
            >
                <b-spinner v-if="loading" small class="mr-1" />새로고침
            </b-button>
        </div>

        <b-alert v-if="error" show variant="danger" class="d-flex align-items-center justify-content-between">
            <span>{{ error }}</span>
            <b-button size="sm" variant="outline-danger" @click="loadCycles">다시 불러오기</b-button>
        </b-alert>

        <div v-if="loading && cycles.length === 0" class="text-center text-muted py-5">
            <b-spinner small class="mr-2" />실행 이력을 불러오는 중입니다.
        </div>

        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
        <b-table v-else
                 :items="cycles"
                 :fields="fields"
                 table-class="ds-table ds-table-fixed rulebook-cycle-table"
                 responsive
                 hover
                 :show-empty="!error"
                 empty-text="아직 실행된 회차가 없습니다."
        >
            <template #cell(createdAt)="row">
                {{ formatDate(row.item.createdAt) }}
            </template>
            <template #cell(status)="row">
                <span class="ds-status" :class="`ds-status-${statusMeta(row.item).variant}`">{{ statusMeta(row.item).label }}</span>
            </template>
            <template #cell(decision)="row">
                <span class="ds-ellipsis" :title="decisionLabel(row.item)">{{ decisionLabel(row.item) }}</span>
            </template>
            <template #cell(sourceCount)="row">
                {{ row.item.sourceCount }}
            </template>
            <!-- 처분 4종을 한 칸 한 줄로(숫자마다 열을 나누면 11열이 된다) -->
            <template #cell(dispositions)="row">
                <span class="ds-ellipsis" :title="dispositionsText(row.item)">{{ dispositionsText(row.item) }}</span>
            </template>
            <!-- 버전 뒤에 그 버전의 상태를 글자색으로 -->
            <template #cell(version)="row">
                <span class="ds-ellipsis" :title="versionTitle(row.item)">{{ versionLabel(row.item) }} <span v-if="row.item.version" class="ds-status" :class="`ds-status-${versionStatusMeta(row.item.version.status).variant}`">{{ versionStatusMeta(row.item.version.status).label }}</span></span>
            </template>
            <template #cell(errorCode)="row">
                <span class="ds-ellipsis" :title="errorCodeLabel(row.item)">{{ errorCodeLabel(row.item) }}</span>
            </template>
            <!-- 펼침(아이콘 28) — 아래 펼친 상세 행은 44 고정 대상이 아니다 -->
            <template #cell(actions)="row">
                <button type="button"
                        class="ds-row-icon-btn"
                        :aria-expanded="row.detailsShowing ? 'true' : 'false'"
                        :aria-label="row.detailsShowing ? '상세 접기' : '상세 펼치기'"
                        :title="row.detailsShowing ? '상세 접기' : '상세 펼치기'"
                        @click="row.toggleDetails"
                >
                    <AppIcon :name="row.detailsShowing ? 'chevron-down' : 'chevron-right'"/>
                </button>
            </template>
            <template #row-details="row">
                <div class="cycle-detail">
                    <div class="detail-field">
                        <span class="field-label">편집 판단 근거</span>
                        <pre v-if="hasText(row.item.reasoning)"
                             class="text-block"
                             v-text="row.item.reasoning"
                        />
                        <div v-else class="text-muted empty-value">—</div>
                    </div>
                    <div class="detail-field">
                        <span class="field-label">리뷰 결과</span>
                        <template v-if="row.item.review">
                            <div class="mb-1">
                                <b-badge :variant="reviewMeta(row.item).variant">
                                    {{ reviewMeta(row.item).label }}
                                </b-badge>
                            </div>
                            <pre v-if="hasText(row.item.review.reason)"
                                 class="text-block"
                                 v-text="row.item.review.reason"
                            />
                        </template>
                        <div v-else class="text-muted empty-value">—</div>
                    </div>
                    <div class="detail-field">
                        <span class="field-label">안전 점검</span>
                        <pre v-if="hasText(row.item.safetyFindings)"
                             class="text-block"
                             v-text="row.item.safetyFindings"
                        />
                        <div v-else class="text-muted empty-value">—</div>
                    </div>
                </div>
            </template>
        </b-table>
    </section>
</template>

<script>
import {
    DISPOSITION_KEYS,
    apiErrorMessage,
    cycleStatusMeta,
    decisionMeta,
    formatKstDateTime,
    normalizeCycleList,
    reviewVerdictMeta,
    versionStatusMeta,
} from './rulebook-presentation';

const CYCLE_LIMIT = 50;
const EMPTY_LABEL = '—';

export default {
    name: 'RulebookCyclesTab',
    data() {
        return {
            loading: false,
            error: '',
            cycles: [],
            // 폭은 머리가 정한다(ds-table-fixed) — 폭을 안 적은 오류 코드 칸이 남는 폭을 갖는다
            fields: [
                {key: 'createdAt', label: '시각(KST)', tdClass: 'ds-cell-nowrap', thStyle: {width: '140px'}},
                {key: 'status', label: '상태', thStyle: {width: '100px'}},
                {key: 'decision', label: '결정', thStyle: {width: '100px'}},
                {key: 'sourceCount', label: '소스', thClass: 'text-right', tdClass: 'ds-cell-num', thStyle: {width: '64px'}},
                {key: 'dispositions', label: '처분', thStyle: {width: '260px'}},
                {key: 'version', label: '생성 버전', thStyle: {width: '120px'}},
                {key: 'errorCode', label: '오류 코드'},
                // 아이콘 1개 28 + 좌우 16
                {key: 'actions', label: '동작', thClass: 'text-right', tdClass: 'text-right', thStyle: {width: '60px'}},
            ],
        };
    },
    created() {
        this.loadCycles();
    },
    methods: {
        formatDate(value) {
            return formatKstDateTime(value);
        },
        statusMeta(cycle) {
            return cycleStatusMeta(cycle.status);
        },
        versionStatusMeta,
        decisionLabel(cycle) {
            return decisionMeta(cycle.decision).label;
        },
        dispositionLabel(key) {
            return decisionMeta(key).label;
        },
        /** '추가 2 · 갱신 1 · 이미 반영 5 · 보류 0' — 칸과 title 에 같은 값 */
        dispositionsText(cycle) {
            const counts = cycle.dispositions || {};
            return DISPOSITION_KEYS.map((key) => `${this.dispositionLabel(key)} ${counts[key]}`).join(' · ');
        },
        reviewMeta(cycle) {
            return reviewVerdictMeta(cycle.review ? cycle.review.verdict : '');
        },
        versionLabel(cycle) {
            if (!cycle.version) return EMPTY_LABEL;
            return cycle.version.versionNo === null ? `#${cycle.version.id}` : `v${cycle.version.versionNo}`;
        },
        versionTitle(cycle) {
            if (!cycle.version) return EMPTY_LABEL;
            return `${this.versionLabel(cycle)} ${versionStatusMeta(cycle.version.status).label}`;
        },
        errorCodeLabel(cycle) {
            return cycle.errorCode || EMPTY_LABEL;
        },
        /** 값이 있을 때만 본문 박스를 그린다. 없으면 대시 하나만 보인다. */
        hasText(value) {
            return typeof value === 'string' && value.trim() !== '';
        },
        async loadCycles() {
            if (this.loading) return;
            this.loading = true;
            this.error = '';
            try {
                const {data} = await this.$axios.get('/eval-prompt/rulebook/cycles', {
                    params: {limit: CYCLE_LIMIT},
                });
                this.cycles = normalizeCycleList(data).cycles;
            } catch (err) {
                // 재조회 실패는 직전 이력을 지우지 않는다.
                this.error = apiErrorMessage(err, '실행 이력을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.field-label {
    color: #52606d;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

/* 고정 열 합 844(시각 140 · 상태 100 · 결정 100 · 소스 64 · 처분 260 · 생성 버전 120 · 동작 60) + 오류 코드 ~116.
   1366 화면 · 좌측 메뉴 펼침의 탭 본문 폭(약 1024 = 1074 - 카드 테두리 2 - 탭 여백 48) 안 — 더 좁으면 가로 스크롤 */
::v-deep .rulebook-cycle-table {
    min-width: 960px;
}

.cycle-detail {
    background: #fafbfc;
    border-radius: 6px;
    padding: 1rem;
}

.detail-field + .detail-field {
    margin-top: 1rem;
}

.text-block {
    background: #fff;
    border: 1px solid #e5e9ee;
    border-radius: 6px;
    color: #354052;
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 0.35rem 0 0;
    max-height: 16rem;
    overflow: auto;
    padding: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
